import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";
import "./Occupancy.css";
import { lotDailyDataService, lotService } from "../../utils/api";
import { motion, useSpring, useTransform } from "framer-motion";
import { createPortal } from "react-dom";
import { useLotAnalytics, useLotHourlyData } from "../../hooks/useLotData";
import { useMultiLotAnalytics, useMultiLotHourlyData, useMultiLotOccupancy } from "../../hooks/useMultiLotData";
import { useMultiLot } from "../../contexts/MultiLotContext";
import { parseDateAsUTC, getTodayUTC, isSameUTCDay } from "../../utils/dateUtils";

interface DashboardContext {
    currentRange: { startDate: string; endDate: string };
    previousRange: { startDate: string; endDate: string };
    timeframe: "day" | "week" | "month" | "year";
    setTimeframe: (timeframe: "day" | "week" | "month" | "year") => void;
    onDateRangeChange: (currentRange: { startDate: string; endDate: string }, previousRange: { startDate: string; endDate: string }) => void;
    selectedDate: Date;
    setSelectedDate: (date: Date) => void;
    showToday: boolean;
    setShowToday: (value: boolean) => void;
}

interface HourlyData {
    lotId: string;
    timestamp: string;
    totalVehicles: number;
    totalVehiclesThisHour?: number;
    averageOccupancy?: number;
    occupancyCount?: number;
    peakTime?: string;
    peakHour?: string;
}

// Add helper function to format ordinal numbers
const getOrdinal = (n: number) => {
    const j = n % 10,
        k = n % 100;
    if (j === 1 && k !== 11) {
        return n + "st";
    }
    if (j === 2 && k !== 12) {
        return n + "nd";
    }
    if (j === 3 && k !== 13) {
        return n + "rd";
    }
    return n + "th";
};

// Helper function to check if screen is mobile
const isMobile = () => window.innerWidth < 480;

/* Added helper functions for time string parsing and formatting */
const parseTimeString = (timeStr: string): number => {
    const parts = timeStr.split(" ");
    if (parts.length !== 2) return 0;
    const [timePart, period] = parts;
    const [hourStr, minuteStr] = timePart.split(":");
    let hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr, 10);
    if (period === "PM" && hour !== 12) hour += 12;
    if (period === "AM" && hour === 12) hour = 0;
    return hour * 60 + minute;
};

const formatTimeFromMinutes = (minutes: number): string => {
    const hour24 = Math.floor(minutes / 60) % 24;
    const minute = Math.round(minutes % 60);
    const period = hour24 >= 12 ? "PM" : "AM";
    let hour12 = hour24 % 12;
    if (hour12 === 0) hour12 = 12;
    const minuteStr = minute.toString().padStart(2, "0");

    if (isMobile()) {
        return `${hour12}${period}`;
    }
    return `${hour12}:${minuteStr} ${period}`;
};

// Added AnimatedText component to display MotionValue content
const AnimatedText: React.FC<{ value: any }> = ({ value }) => {
    const [display, setDisplay] = useState(value.get());
    useEffect(() => {
        const unsubscribe = value.on('change', setDisplay);
        return () => unsubscribe && unsubscribe();
    }, [value]);
    return <>{display}</>;
};

const Occupancy: React.FC = () => {
    const { lotId } = useParams<{ lotId: string }>();
    const { currentRange, previousRange, timeframe, setTimeframe, onDateRangeChange, selectedDate, setSelectedDate, showToday, setShowToday } = useOutletContext<DashboardContext>();
    const [isGraphLoading, setIsGraphLoading] = useState(false);
    const [lotName, setLotName] = useState<string>("...");
    const [lotCapacity, setLotCapacity] = useState<number>(0);
    const [occupancyData, setOccupancyData] = useState<HourlyData[]>([]);
    const [currentOccupancy, setCurrentOccupancy] = useState<number | null>(null);
    const [peakInfo, setPeakInfo] = useState<{ value: number; time: string } | null>(null);
    const [dynamicMetric, setDynamicMetric] = useState<{ label: string; value: string; subtext?: string } | null>(null);
    const [verticalLineStyle, setVerticalLineStyle] = useState<React.CSSProperties | null>(null);

    // Multi-lot context
    const { isMultiLotMode, selectedLots, getSelectedLotIds } = useMultiLot();
    const lotIds = isMultiLotMode ? getSelectedLotIds() : [lotId || ""];

    // Single-lot hooks
    const singleLotHourlyData = useLotHourlyData(
        !isMultiLotMode ? (lotId || "") : "",
        currentRange.startDate,
        timeframe
    );

    const singleLotAnalyticsData = useLotAnalytics(
        !isMultiLotMode ? (lotId || "") : "",
        currentRange.startDate,
        currentRange.endDate,
        timeframe
    );

    // Multi-lot hooks
    const multiLotHourlyData = useMultiLotHourlyData(
        isMultiLotMode ? lotIds : [],
        currentRange.startDate,
        timeframe
    );

    const multiLotAnalyticsData = useMultiLotAnalytics(
        isMultiLotMode ? lotIds : [],
        currentRange.startDate,
        currentRange.endDate,
        timeframe,
        selectedLots
    );

    const multiLotOccupancy = useMultiLotOccupancy(isMultiLotMode ? lotIds : []);

    // Select appropriate data based on mode
    const hourlyData = isMultiLotMode ? multiLotHourlyData.data : singleLotHourlyData.data;
    const isHourlyLoading = isMultiLotMode ? multiLotHourlyData.isLoading : singleLotHourlyData.isLoading;
    const isHourlyFetching = isMultiLotMode ? multiLotHourlyData.isLoading : singleLotHourlyData.isFetching;

    const analyticsData = isMultiLotMode ? multiLotAnalyticsData.data : singleLotAnalyticsData.data;
    const isAnalyticsLoading = isMultiLotMode ? multiLotAnalyticsData.isLoading : singleLotAnalyticsData.isLoading;
    const isAnalyticsFetching = isMultiLotMode ? multiLotAnalyticsData.isLoading : singleLotAnalyticsData.isFetching;

    // Added animated springs for metrics using framer-motion constants similar to Revenue
    const animatedPeakTime = useSpring(0, { stiffness: 10000, damping: 600 });
    const animatedPeakCount = useSpring(0, { stiffness: 10000, damping: 600 });
    const animatedDynamicCount = useSpring(0, { stiffness: 10000, damping: 600 });

    const displayPeakTime = useTransform(animatedPeakTime, (val) => formatTimeFromMinutes(Math.round(val)));
    const displayPeakCount = useTransform(animatedPeakCount, (val) => Math.round(val).toString());
    const displayDynamicCount = useTransform(animatedDynamicCount, (val) => Math.round(val).toString());

    useEffect(() => {
        if (peakInfo && peakInfo.time && peakInfo.time !== "-") {
            const targetMinutes = parseTimeString(peakInfo.time);
            animatedPeakTime.set(targetMinutes);
        }
        if (peakInfo && typeof peakInfo.value === "number") {
            animatedPeakCount.set(peakInfo.value);
        }
    }, [peakInfo]);

    useEffect(() => {
        if (dynamicMetric && dynamicMetric.value && dynamicMetric.value !== "-") {
            const match = dynamicMetric.value.match(/^(\d+)/);
            if (match) {
                animatedDynamicCount.set(parseInt(match[1], 10));
            }
        }
    }, [dynamicMetric]);

    // Robust local date comparison for isTodayView
    const [startYear, startMonth, startDay] = currentRange.startDate.split('-').map(Number);
    const selectedDateObj = new Date(startYear, startMonth - 1, startDay);
    const now = new Date();
    const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const isTodayView = timeframe === "day" && selectedDateObj.getTime() === todayDate.getTime();

    const currentLotCapacity = useMemo(() => {
        if (isMultiLotMode) {
            return selectedLots.reduce((sum, lot: any) => sum + (Number(lot?.lotCapacity) || 0), 0);
        }
        return lotCapacity;
    }, [isMultiLotMode, selectedLots, lotCapacity]);

    const getOccupancyCount = (entry: any, fallback = 0) => {
        const averageOccupancy = Number(entry?.averageOccupancy);
        if (Number.isFinite(averageOccupancy) && currentLotCapacity > 0) {
            return Math.round((averageOccupancy / 100) * currentLotCapacity);
        }
        return Number(fallback) || 0;
    };

    // Fetch lot name (no longer fetches occupancy here)
    useEffect(() => {
        const fetchLotInfo = async () => {
            if (!lotId) return;
            try {
                const lotData = await lotService.getLotById(lotId);
                setLotName(lotData.lotName || lotData?.data?.lotName || "Unknown Lot");
                setLotCapacity(Number(lotData.lotCapacity ?? lotData?.data?.lotCapacity) || 0);
            } catch (error) {
                setLotName("Unknown Lot");
                setLotCapacity(0);
            }
        };
        fetchLotInfo();
    }, [lotId]);

    // Fetch current occupancy for day, week, and month views using lotService
    useEffect(() => {
        const getCurrent = async () => {
            if (!lotId || (timeframe !== "day" && timeframe !== "week" && timeframe !== "month")) {
                setCurrentOccupancy(null);
                return;
            }
            if (timeframe === "week" || timeframe === "month") {
                const todayUTC = getTodayUTC();
                const start = parseDateAsUTC(currentRange.startDate);
                const end = parseDateAsUTC(currentRange.endDate);
                if (todayUTC < start || todayUTC > end) {
                    setCurrentOccupancy(null);
                    return;
                }
            }
            const occ = await lotService.getCurrentOccupancy(lotId);
            setCurrentOccupancy(occ);
        };
        getCurrent();
    }, [lotId, timeframe, currentRange.startDate, currentRange.endDate]);

    // Fetch occupancy data for the selected timeframe
    useEffect(() => {
        const processOccupancyData = () => {
            if (!lotId || !currentRange.startDate || !currentRange.endDate) return;
            try {
                let data: HourlyData[] = [];
                if (timeframe === "day" && currentRange.startDate === currentRange.endDate) {
                    // Use hourly data for day view
                    if (hourlyData?.data && hourlyData.data.length > 0) {
                        data = hourlyData.data.map((entry: any) => ({
                            ...entry,
                            totalVehicles: entry.totalVehiclesThisHour ?? entry.totalVehicles,
                            averageOccupancy: entry.averageOccupancy,
                            occupancyCount: getOccupancyCount(entry, entry.totalVehiclesThisHour ?? entry.totalVehicles)
                        }));
                    } else if (analyticsData && analyticsData.length > 0) {
                        // Fallback to daily data
                        data = [
                            {
                                lotId: analyticsData[0].lotId,
                                timestamp: analyticsData[0].date,
                                totalVehicles: analyticsData[0].totalVehicles,
                                averageOccupancy: analyticsData[0].averageOccupancy,
                                occupancyCount: getOccupancyCount(analyticsData[0], analyticsData[0].totalVehicles),
                                peakHour: analyticsData[0].peakHour,
                                peakTime: analyticsData[0].peakTime,
                            }
                        ];
                    }
                } else {
                    // Use analytics data for week/month/year views
                    data = analyticsData?.map((entry: any) => ({
                        lotId: entry.lotId,
                        timestamp: entry.date,
                        totalVehicles: entry.totalVehicles,
                        averageOccupancy: entry.averageOccupancy,
                        occupancyCount: getOccupancyCount(entry, entry.totalVehicles),
                        peakHour: entry.peakHour,
                        peakTime: entry.peakTime,
                    })) || [];
                }
                setOccupancyData(data);
            } catch (error) {
                console.error("Error processing occupancy data:", error);
                setOccupancyData([]);
            }
        };
        processOccupancyData();
    }, [lotId, currentRange, timeframe, hourlyData, analyticsData, currentLotCapacity]);

    // Calculate metrics (peak time/hour, dynamic metric)
    useEffect(() => {
        if (!occupancyData || occupancyData.length === 0) {
            setPeakInfo(null);
            setDynamicMetric(null);
            return;
        }
        // Find peak
        let peakIdx = 0;
        let maxVehicles = occupancyData[0].occupancyCount ?? occupancyData[0].totalVehicles;
        occupancyData.forEach((entry, idx) => {
            const occupancyCount = entry.occupancyCount ?? entry.totalVehicles;
            if (occupancyCount > maxVehicles) {
                maxVehicles = occupancyCount;
                peakIdx = idx;
            }
        });
        const peakEntry = occupancyData[peakIdx];

        // --- PEAK TIME/HOUR LOGIC ---
        let peakTimeLabel = "-";
        if (timeframe === "day") {
            // Use peakTime from the data (hourly or fallback daily)
            const peakTimeStr = peakEntry.peakTime;
            if (peakTimeStr) {
                const d = new Date(peakTimeStr);
                if (!isNaN(d.getTime())) {
                    peakTimeLabel = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
                }
            }
        } else if (timeframe === "week" || timeframe === "month" || timeframe === "year") {
            // Average all peakHour times in the data (parse as HH:mm:ss)
            const validPeakHours = occupancyData
                .map(e => e.peakHour)
                .filter(Boolean)
                .map(str => {
                    // Parse 'HH:mm:ss' as ms since midnight
                    const [h, m, s] = str!.split(":").map(Number);
                    if ([h, m, s].some(isNaN)) return null;
                    return h * 3600000 + m * 60000 + s * 1000;
                })
                .filter(ms => ms !== null) as number[];
            if (validPeakHours.length > 0) {
                const avgMs = Math.round(validPeakHours.reduce((a, b) => a + b, 0) / validPeakHours.length);
                // Create a Date at UTC midnight and add avgMs
                const avgDate = new Date(Date.UTC(1970, 0, 1, 0, 0, 0, 0) + avgMs);
                peakTimeLabel = avgDate.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
            }
        }
        setPeakInfo({ value: maxVehicles, time: peakTimeLabel });

        // --- DYNAMIC METRIC LOGIC (updated for week/month/year) ---
        if (timeframe === "day") {
            // Determine if selected day is today (compare as UTC)
            const selectedDate = parseDateAsUTC(currentRange.startDate);
            const todayUTC = getTodayUTC();
            if (isSameUTCDay(selectedDate, todayUTC)) {
                setDynamicMetric({ label: "Currently", value: (currentOccupancy !== null && currentOccupancy !== undefined) ? `${currentOccupancy} Occupants` : "-", subtext: "Current vehicles parked in your lot" });
            } else {
                // Calculate total vehicles using the same logic as the subtitle
                let total = 0;
                if (timeframe === "day" && hourlyData?.data && hourlyData.data.length > 0) {
                    total = hourlyData.data.reduce((sum: number, entry: any) => sum + Number(entry.totalVehicles || 0), 0);
                } else if (analyticsData && analyticsData.length > 0) {
                    total = analyticsData.reduce((sum: number, entry: any) => sum + Number(entry.totalVehicles || 0), 0);
                }
                const fullDate = selectedDate.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric", timeZone: "UTC" });
                if (isMobile()) {
                    setDynamicMetric({ label: "Total Vehicles", value: `${total}`, subtext: `${fullDate}` });
                } else {
                    setDynamicMetric({ label: "Total Vehicles", value: `${total} Occupants`, subtext: `${fullDate}` });
                }
            }
        } else if (timeframe === "week" || timeframe === "month") {
            // Peak Day: find entry with highest totalVehicles from analytics data
            let peakDayEntry: any = null;
            let peakDayVehicles = 0;

            if (analyticsData && analyticsData.length > 0) {
                analyticsData.forEach((entry: any) => {
                    const totalVehicles = Number(entry.totalVehicles || 0);
                    if (totalVehicles > peakDayVehicles) {
                        peakDayVehicles = totalVehicles;
                        peakDayEntry = entry;
                    }
                });
            }

            let peakDaySubtext = "-";
            if (peakDayEntry && peakDayEntry.date) {
                const d = parseDateAsUTC(peakDayEntry.date);
                peakDaySubtext = d.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric", timeZone: "UTC" });
            }
            setDynamicMetric({ label: "Peak Day", value: peakDaySubtext, subtext: `Total: ${peakDayVehicles} Occupants` });
        } else if (timeframe === "year") {
            // Peak Month: group by month, sum totalVehicles, find month with highest sum
            const monthMap: { [key: string]: { sum: number, date: string } } = {};

            if (analyticsData && analyticsData.length > 0) {
                analyticsData.forEach((entry: any) => {
                    const d = parseDateAsUTC(entry.date);
                    const key = `${d.getUTCFullYear()}-${d.getUTCMonth()}`;
                    if (!monthMap[key]) {
                        monthMap[key] = { sum: 0, date: entry.date };
                    }
                    monthMap[key].sum += Number(entry.totalVehicles || 0);
                });
            }

            let peakMonthKey = null;
            let peakMonthSum = -Infinity;
            Object.entries(monthMap).forEach(([key, val]) => {
                if (val.sum > peakMonthSum) {
                    peakMonthSum = val.sum;
                    peakMonthKey = key;
                }
            });
            let peakMonthLabel = "-";
            if (peakMonthKey && monthMap[peakMonthKey]) {
                const d = parseDateAsUTC(monthMap[peakMonthKey].date);
                peakMonthLabel = d.toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
            }
            setDynamicMetric({ label: "Peak Month", value: peakMonthLabel, subtext: `Total: ${peakMonthSum} Occupants` });
        }
    }, [occupancyData, currentOccupancy, timeframe, currentRange]);

    // Graph data formatting using useMemo
    const processedGraphData = useMemo(() => {
        if (!occupancyData || occupancyData.length === 0) {
            return [];
        }

        try {
            let processedData;
            if (timeframe === "year") {
                // Group by month: average the daily average occupancy counts.
                const monthMap: { [key: string]: { occupancySum: number, totalVehicles: number, count: number, date: string } } = {};
                occupancyData.forEach(entry => {
                    try {
                        const d = parseDateAsUTC(entry.timestamp);
                        if (isNaN(d.getTime())) {
                            console.warn('Invalid date:', entry.timestamp);
                            return;
                        }
                        const key = `${d.getUTCFullYear()}-${d.getUTCMonth()}`;
                        if (!monthMap[key]) {
                            monthMap[key] = { occupancySum: 0, totalVehicles: 0, count: 0, date: entry.timestamp };
                        }
                        monthMap[key].occupancySum += entry.occupancyCount ?? entry.totalVehicles ?? 0;
                        monthMap[key].totalVehicles += entry.totalVehicles || 0;
                        monthMap[key].count += 1;
                    } catch (error) {
                        console.warn('Error processing entry:', entry, error);
                    }
                });
                processedData = Object.values(monthMap)
                    .map(({ occupancySum, totalVehicles, count, date }) => {
                        try {
                            const d = parseDateAsUTC(date);
                            if (isNaN(d.getTime())) {
                                console.warn('Invalid date:', date);
                                return null;
                            }
                            return {
                                time: d.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" }),
                                actualDate: d.toISOString(),
                                totalVehicles,
                                occupancyCount: count > 0 ? Math.round(occupancySum / count) : 0
                            };
                        } catch (error) {
                            console.warn('Error processing month data:', date, error);
                            return null;
                        }
                    })
                    .filter(item => item !== null);
            } else if (timeframe === "day") {
                // Generate 24 hourly data points for the day
                const formatHour = (hour: number) => {
                    if (hour === 0) return "12AM";
                    if (hour < 12) return hour + "AM";
                    if (hour === 12) return "12PM";
                    return (hour - 12) + "PM";
                };
                const hourlyDataArray = [];
                for (let hour = 0; hour < 24; hour++) {
                    const matchingEntry = occupancyData.find(e => {
                        const d = new Date(e.timestamp);
                        return d.getHours() === hour;
                    });
                    hourlyDataArray.push({
                        time: formatHour(hour),
                        totalVehicles: matchingEntry ? matchingEntry.totalVehicles : 0,
                        occupancyCount: matchingEntry ? (matchingEntry.occupancyCount ?? matchingEntry.totalVehicles) : 0
                    });
                }
                if (isTodayView) {
                    const currentHour = new Date().getHours();
                    if (currentHour >= 0 && currentHour < 24) {
                        hourlyDataArray[currentHour] = {
                            ...hourlyDataArray[currentHour],
                            totalVehicles: (currentOccupancy !== null && currentOccupancy !== undefined) ? currentOccupancy : hourlyDataArray[currentHour].totalVehicles,
                            occupancyCount: (currentOccupancy !== null && currentOccupancy !== undefined) ? currentOccupancy : hourlyDataArray[currentHour].occupancyCount,
                            isCurrent: true
                        };
                    }
                }
                processedData = hourlyDataArray;
            } else {
                // Week or month views: show each day
                const sortedData = occupancyData.slice().sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
                const now = new Date();
                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                const rangeStart = parseDateAsUTC(currentRange.startDate);
                const rangeEnd = parseDateAsUTC(currentRange.endDate);
                const filteredData = sortedData.filter(entry => {
                    const entryDate = parseDateAsUTC(entry.timestamp);
                    return entryDate >= rangeStart && entryDate <= rangeEnd;
                });
                let data = filteredData.map(entry => {
                    const d = parseDateAsUTC(entry.timestamp);
                    let label = "";
                    if (timeframe === "month") {
                        const day = d.getDate();
                        if (day === 1) { label = "Week 1"; } else if (day === 8) { label = "Week 2"; } else if (day === 15) { label = "Week 3"; } else if (day === 22) { label = "Week 4"; } else if (day === 29) { label = "Week 5"; }
                    } else {
                        label = d.toLocaleDateString("en-US", { weekday: "short" });
                    }
                    if (d.getTime() === today.getTime() && currentOccupancy !== null) {
                        return {
                            time: label,
                            totalVehicles: currentOccupancy,
                            occupancyCount: currentOccupancy,
                            isCurrent: true,
                            actualDate: d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }),
                            rawTimestamp: d.getTime()
                        };
                    } else {
                        return {
                            time: label,
                            totalVehicles: entry.totalVehicles,
                            occupancyCount: entry.occupancyCount ?? entry.totalVehicles,
                            actualDate: d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }),
                            rawTimestamp: d.getTime()
                        };
                    }
                });
                const hasToday = sortedData.some(entry => {
                    const d = parseDateAsUTC(entry.timestamp);
                    return d.getTime() === today.getTime();
                });
                if (today.getTime() >= rangeStart.getTime() && today.getTime() <= rangeEnd.getTime() && !hasToday) {
                    let todayLabel = "";
                    if (timeframe === "month") {
                        const day = today.getDate();
                        if (day === 1) { todayLabel = "Week 1"; } else if (day === 8) { todayLabel = "Week 2"; } else if (day === 15) { todayLabel = "Week 3"; } else if (day === 22) { todayLabel = "Week 4"; } else if (day === 29) { todayLabel = "Week 5"; }
                    } else {
                        todayLabel = today.toLocaleDateString("en-US", { weekday: "short" });
                    }
                    data.push({
                        time: todayLabel,
                        totalVehicles: currentOccupancy !== null ? currentOccupancy : 0,
                        occupancyCount: currentOccupancy !== null ? currentOccupancy : 0,
                        isCurrent: true,
                        actualDate: today.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }),
                        rawTimestamp: today.getTime()
                    });
                    data.sort((a, b) => a.rawTimestamp - b.rawTimestamp);
                }
                if (timeframe === "month" && data.length > 0) {
                    data[0].time = "Week 1";
                }
                processedData = data.map(({ rawTimestamp, ...rest }) => rest);
            }
            return processedData;
        } catch (error) {
            console.error('Error in graphData calculation:', error);
            return [];
        }
    }, [occupancyData, timeframe, currentRange, currentOccupancy, isTodayView]);

    // Dispatch graph data update when processedGraphData changes
    useEffect(() => {
        window.dispatchEvent(new CustomEvent('graphDataUpdate', {
            detail: { graphData: processedGraphData }
        }));
    }, [processedGraphData]);

    // Use processedGraphData as graphData for rendering
    const graphData = processedGraphData;

    // Add effect to handle graph loading state
    useEffect(() => {
        setIsGraphLoading(true);
        const timer = setTimeout(() => {
            setIsGraphLoading(false);
        }, 500);
        return () => clearTimeout(timer);
    }, [timeframe, occupancyData]);

    // New useEffect: Calculate the absolute position for the vertical line overlay
    useEffect(() => {
        function updateVerticalLine() {
            if (timeframe === "day" && isTodayView && !isGraphLoading) {
                const container = document.querySelector('.occupancy-graph-container');
                if (container) {
                    const rect = container.getBoundingClientRect();
                    const currentHour = new Date().getHours();
                    // Calculate center of current bar as (currentHour + 0.5)/24 relative to container width
                    const leftRelative = ((currentHour + 0.5) / 24) * rect.width;
                    const leftAbsolute = rect.left + leftRelative;
                    setVerticalLineStyle({
                        position: 'absolute',
                        top: rect.top + 'px',
                        left: leftAbsolute + 'px',
                        height: '300px',
                        width: '2px',
                        borderLeft: '2px dashed var(--stroke-darker-grey)',
                        pointerEvents: 'none'
                    });
                }
            } else {
                setVerticalLineStyle(null);
            }
        }
        updateVerticalLine();
        window.addEventListener('resize', updateVerticalLine);
        return () => window.removeEventListener('resize', updateVerticalLine);
    }, [timeframe, isTodayView, isGraphLoading]);

    // Custom XAxis tick for day view with fixed labels
    const allowedTicks = ["12AM", "3AM", "6AM", "9AM", "12PM", "3PM", "6PM", "9PM"];

    const dayTick = (props: any) => {
        const { x, y, payload } = props;
        if (!allowedTicks.includes(payload.value)) return <></>;
        return (
            <text x={x} y={y + 16} textAnchor="middle" fill="var(--text-medium)" fontSize={13} fontWeight={500}>
                {payload.value}
            </text>
        );
    };

    // Default tick for non-day views
    const xAxisTick = (props: any) => {
        const { x, y, payload } = props;
        if (payload.value === "") return <></>;
        return (
            <text x={x} y={y + 16} textAnchor="middle" fill="var(--text-medium)" fontSize={13} fontWeight={500}>
                {payload.value}
            </text>
        );
    };

    // Custom tooltip component to display actual date on hover (for month view, shows actual date instead of week label)
    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            let line1 = "";
            let line2 = "";

            if (timeframe === "day") {
                // Ensure time label includes minutes
                if (!label.includes(":")) {
                    const period = label.slice(-2);
                    const hour = label.slice(0, -2);
                    line1 = `${hour}:00 ${period}`;
                } else {
                    line1 = label;
                }
                line2 = `Occupants: ${payload[0].value}`;
            } else if (timeframe === "week" || timeframe === "month") {
                const date = new Date(payload[0].payload.actualDate);
                const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
                const month = date.toLocaleDateString("en-US", { month: "long" });
                const day = date.getDate();
                const dayWithOrdinal = getOrdinal(day);
                const year = date.getFullYear();
                line1 = `${weekday}, ${month} ${dayWithOrdinal}, ${year}`;
                line2 = `Occupants: ${payload[0].value}`;
            } else if (timeframe === "year") {
                const date = new Date(payload[0].payload.actualDate);
                line1 = date.toLocaleDateString("en-US", { month: "long" });
                line2 = `Average Occupants: ${payload[0].value}`;
            }

            return (
                <div className="occupancy-tooltip-box">
                    <div className="occupancy-tooltip-line1">{line1}</div>
                    <div className="occupancy-tooltip-line2">{line2}</div>
                </div>
            );
        }
        return null;
    };

    // Custom shape for current time bar with vertical line
    const renderCustomizedBar = (props: any) => {
        const { x, y, width, height, fill, payload, onClick } = props;
        let verticalLine = null;
        if (payload && payload.isCurrent) {
            const currentMinute = new Date().getUTCMinutes();
            let lineX = x;
            if (currentMinute >= 0 && currentMinute < 20) {
                lineX = x; // left aligned
            } else if (currentMinute >= 20 && currentMinute < 40) {
                lineX = x + width / 2; // center aligned
            } else if (currentMinute >= 40) {
                lineX = x + width; // right aligned
            }
            // Use y offset of 15 instead of 10 and ensure it doesn't go negative
            verticalLine = <line x1={lineX} y1={Math.max(0, y - 15)} x2={lineX} y2={y} stroke="var(--stroke-darker-grey)" strokeWidth={3} />;
        }
        return (
            <g onClick={onClick} style={{ overflow: 'visible' }}>
                <rect x={x} y={y} width={width} height={height} fill={payload && payload.isCurrent ? "var(--stroke-darker-grey)" : fill} />
                {verticalLine}
            </g>
        );
    };

    // Robust local date title logic from DwellTime
    function getTimeRangeText(timeframe: string, startDate: string, endDate: string) {
        // Parse the input dates
        const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
        const [endYear, endMonth, endDay] = endDate.split('-').map(Number);

        // Create dates in local timezone
        const start = new Date(startYear, startMonth - 1, startDay);
        const end = new Date(endYear, endMonth - 1, endDay);

        // Get current date in local timezone
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        if (timeframe === "day") {
            if (start.getTime() === today.getTime()) {
                return "Occupancy Today";
            }
            const yesterday = new Date(today);
            yesterday.setDate(today.getDate() - 1);
            if (start.getTime() === yesterday.getTime()) {
                return "Occupancy Yesterday";
            }
            return `Occupancy on ${start.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}`;
        }

        if (timeframe === "week") {
            // Calculate current week boundaries
            const currentWeekStart = new Date(today);
            currentWeekStart.setDate(today.getDate() - today.getDay());
            const currentWeekEnd = new Date(currentWeekStart);
            currentWeekEnd.setDate(currentWeekStart.getDate() + 6);

            // Calculate last week boundaries
            const lastWeekStart = new Date(currentWeekStart);
            lastWeekStart.setDate(currentWeekStart.getDate() - 7);
            const lastWeekEnd = new Date(lastWeekStart);
            lastWeekEnd.setDate(lastWeekStart.getDate() + 6);

            if (start.getTime() === currentWeekStart.getTime() && end.getTime() === currentWeekEnd.getTime()) {
                return "Occupancy This Week";
            }
            if (start.getTime() === lastWeekStart.getTime() && end.getTime() === lastWeekEnd.getTime()) {
                return "Occupancy Last Week";
            }
            return `Occupancy for ${start.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`;
        }

        if (timeframe === "month") {
            // Calculate current month boundaries
            const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
            const currentMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);

            // Calculate last month boundaries
            const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);

            if (start.getTime() === currentMonthStart.getTime() && end.getTime() === currentMonthEnd.getTime()) {
                return "Occupancy This Month so far";
            }
            if (start.getTime() === lastMonthStart.getTime() && end.getTime() === lastMonthEnd.getTime()) {
                return "Occupancy Last Month";
            }
            return `Occupancy for ${start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`;
        }

        if (timeframe === "year") {
            // Calculate current year boundaries
            const currentYearStart = new Date(today.getFullYear(), 0, 1);
            const currentYearEnd = new Date(today.getFullYear(), 11, 31);

            // Calculate last year boundaries
            const lastYearStart = new Date(today.getFullYear() - 1, 0, 1);
            const lastYearEnd = new Date(today.getFullYear() - 1, 11, 31);

            if (start.getTime() === currentYearStart.getTime() && end.getTime() === currentYearEnd.getTime()) {
                return "Occupancy This Year so far";
            }
            if (start.getTime() === lastYearStart.getTime() && end.getTime() === lastYearEnd.getTime()) {
                return "Occupancy Last Year";
            }
            return `Occupancy in ${start.getFullYear()}`;
        }

        return "Occupancy";
    }

    const handleBarClick = (data: any) => {
        if (!data || !data.actualDate) return;

        const clickedDate = new Date(data.actualDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Do not allow future dates or if already in day view
        if (clickedDate > today || timeframe === "day") return;

        if (timeframe === "year") {
            const utcYear = clickedDate.getUTCFullYear();
            const utcMonth = clickedDate.getUTCMonth();
            const newDate = new Date(utcYear, utcMonth, 1);
            setTimeframe("month");
            setSelectedDate(newDate);
            setShowToday(false);
        } else if (timeframe === "week" || timeframe === "month") {
            // In week/month view, switch to day view and update the shared selected date
            setTimeframe("day");
            setSelectedDate(clickedDate);
            setShowToday(false);
        }
    };

    let portalContent: React.ReactNode = null;
    if (verticalLineStyle) {
        const currentHour = new Date().getHours();
        const labelSide = currentHour >= 20 ? 'left' : 'right';
        const labelInlineStyle: React.CSSProperties = {
            top: verticalLineStyle.top,
            left: labelSide === 'right'
                ? (parseFloat(verticalLineStyle.left as string) + 6) + 'px'
                : (parseFloat(verticalLineStyle.left as string) - 6) + 'px'
        };
        portalContent = (
            <div>
                <div className="occupancy-current-line" style={verticalLineStyle}></div>
                <div className={`occupancy-current-label ${labelSide}`} style={labelInlineStyle}>
                    <div>Currently</div>
                    <div>{new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}</div>
                </div>
            </div>
        );
    }

    const dynamicMetricContent = dynamicMetric ? (
        !isNaN(parseInt(dynamicMetric.value, 10)) ? (
            <>
                <AnimatedText value={displayDynamicCount} />{dynamicMetric.value.replace(/^\d+/, "")}
            </>
        ) : (
            <motion.span
                key={dynamicMetric.value}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
            >
                {dynamicMetric.value}
            </motion.span>
        )
    ) : ("-");

    const hasData = occupancyData && occupancyData.length > 0;
    const hasHourlySource = Array.isArray(hourlyData?.data) && hourlyData.data.length > 0;
    const hasAnalyticsSource = Array.isArray(analyticsData) && analyticsData.length > 0;
    const hasNoSourceData = timeframe === "day" ? !hasHourlySource && !hasAnalyticsSource : !hasAnalyticsSource;
    const isSkeletonLoading =
        isHourlyLoading ||
        isAnalyticsLoading ||
        isHourlyFetching ||
        isAnalyticsFetching ||
        !hasData ||
        hasNoSourceData;

    if (isSkeletonLoading) {
        return (
            <div className="occupancy-content occupancy-skeleton">
                <div className="occupancy-metrics-row">
                    {Array.from({ length: 2 }).map((_, index) => (
                        <div
                            key={`occupancy-metric-skeleton-${index}`}
                            className="skeleton-block occupancy-skeleton-card"
                            aria-hidden="true"
                        />
                    ))}
                </div>
                <div className="occupancy-graph-container occupancy-skeleton-graph">
                    <div className="skeleton-block occupancy-skeleton-graph-block" aria-hidden="true" />
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="occupancy-content">
                <div className="occupancy-metrics-row">
                    <div className="occupancy-metric-box">
                        <motion.span className="occupancy-metric-value">{peakInfo ? <AnimatedText value={displayPeakTime} /> : "-"}</motion.span>
                        <span className="occupancy-metric-label">Peak Time</span>
                        {isMobile() ? (
                            <span className="occupancy-metric-subtext">
                                with {peakInfo ? <motion.span><AnimatedText value={displayPeakCount} /></motion.span> : "-"} occupants.
                            </span>
                        ) : (
                            <span className="occupancy-metric-subtext">
                                Busiest period with {peakInfo ? <motion.span><AnimatedText value={displayPeakCount} /></motion.span> : "-"} occupants.
                            </span>
                        )}
                    </div>
                    <div className="occupancy-metric-box">
                        <motion.span className="occupancy-metric-value">
                            {dynamicMetricContent}
                        </motion.span>
                        <span className="occupancy-metric-label">{dynamicMetric ? dynamicMetric.label : "-"}</span>
                        <span className="occupancy-metric-subtext">
                            {timeframe === "day" && dynamicMetric && dynamicMetric.subtext
                                ? dynamicMetric.subtext
                                : timeframe === "year"
                                    ? "Month with highest occupancy"
                                    : "Day with highest occupancy"}
                        </span>
                    </div>
                </div>
                <div className="occupancy-graph-container">
                    <div className="occupancy-graph-title">{getTimeRangeText(timeframe, currentRange.startDate, currentRange.endDate)}</div>
                    <div className="occupancy-graph-subtitle">A total of {
                        (() => {
                            if (timeframe === "day" && hourlyData?.data && hourlyData.data.length > 0) {
                                return hourlyData.data.reduce((sum: number, entry: any) => sum + Number(entry.totalVehicles || 0), 0);
                            } else if (analyticsData && analyticsData.length > 0) {
                                return analyticsData.reduce((sum: number, entry: any) => sum + Number(entry.totalVehicles || 0), 0);
                            }
                            return 0;
                        })()
                    } occupants visited your lot.</div>
                    {isGraphLoading ? (
                        <div className="skeleton-block occupancy-graph-loading-block" aria-hidden="true" />
                    ) : (
                        <ResponsiveContainer width="100%" height={isMobile() ? 250 : 325}>
                            <BarChart data={graphData} margin={{ top: 25, right: 0, left: 0, bottom: 0 }}>
                                <XAxis dataKey="time" axisLine={false} tickLine={false} {...(timeframe === "day"
                                    ? { tick: dayTick, ticks: allowedTicks }
                                    : { tick: xAxisTick, interval: timeframe === "month" ? 0 : undefined }
                                )} />
                                <YAxis hide />
                                <Tooltip content={<CustomTooltip />} cursor={false} />
                                {timeframe !== "year" ? (
                                    <Bar dataKey="occupancyCount" radius={[5, 5, 5, 5]} onClick={handleBarClick} style={{ cursor: timeframe === "day" ? "default" : "pointer" }}>
                                        {(graphData as Array<{ time: string; occupancyCount: number; isCurrent?: boolean }>).map((entry, index) => (
                                            entry ? <Cell key={`cell-${index}`} className={entry.isCurrent ? "occupancy-current-bar" : "occupancy-bar"} /> : null
                                        ))}
                                    </Bar>
                                ) : (
                                    <Bar dataKey="occupancyCount" className="occupancy-bar" radius={[5, 5, 5, 5]} onClick={handleBarClick} style={{ cursor: "pointer" }} />
                                )}
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>
            {verticalLineStyle && createPortal(portalContent, document.body)}
        </div>
    );
};

export default Occupancy;
